// 🎯 Helpers para la Migración de Categorías - IDs únicos
// Este archivo maneja la compatibilidad entre el formato antiguo (nombres) y nuevo (UUIDs)

/**
 * ✅ Función helper para obtener nombres de categorías legibles de un proveedor
 * Maneja tanto proveedores migrados como no migrados
 * @param {Object} provider - Objeto del proveedor
 * @returns {Array} Array de nombres de categorías legibles
 */
export const getProviderCategories = (provider) => {
  if (!provider) return [];

  // Priorizar categoryNames si existe (proveedores migrados)
  if (provider.categoryNames && provider.categoryNames.length > 0) {
    return provider.categoryNames;
  }

  // Fallback a categories (proveedores no migrados o compatibilidad)
  return provider.categories || [];
};

/**
 * ✅ Función helper para mostrar subcategorías legibles
 * Extrae la parte legible de subcategorías que pueden estar en formato:
 * - Antiguo: "Jardinería-Poda"
 * - Nuevo: "uuid-1234-abcd-Poda"
 * - Limpio: "Poda"
 * @param {Array} subcategories - Array de subcategorías
 * @returns {Array} Array de nombres de subcategorías legibles
 */
export const getDisplaySubcategories = (subcategories) => {
  if (!subcategories || !Array.isArray(subcategories)) return [];

  return subcategories.map(sub => {
    if (typeof sub !== 'string') return sub;

    // Primero eliminar sufijos de sugerencia
    let cleaned = sub.replace(' (sugerencia)', '').replace(/ \(sugerencia - .*\)$/, '');

    // Si contiene guiones, extraer la parte después del último guión
    if (cleaned.includes('-')) {
      const parts = cleaned.split('-');
      // Para formato "uuid-1234-abcd-Poda" o "Jardinería-Poda"
      return parts[parts.length - 1];
    }

    return cleaned;
  });
};

/**
 * ✅ Función helper para verificar si un proveedor está migrado
 * @param {Object} provider - Objeto del proveedor
 * @returns {Boolean} true si el proveedor está migrado
 */
export const isProviderMigrated = (provider) => {
  return provider &&
         provider.categoryIds &&
         provider.categoryIds.length > 0 &&
         provider.categoryNames &&
         provider.categoryNames.length > 0;
};

/**
 * ✅ Función helper para preparar datos de proveedor para envío al backend
 * Convierte tanto categorías como subcategorías a sus IDs reales
 * @param {Object} providerData - Datos del proveedor
 * @param {Boolean} isUpdate - Si es una actualización de proveedor existente
 * @param {Array} availableCategories - Array de categorías disponibles con IDs
 * @returns {Object} Datos preparados para el backend
 */
export const prepareProviderDataForBackend = (providerData, isUpdate = false, availableCategories = []) => {
  const preparedData = { ...providerData };

  // Crear mapa de nombres de categorías a IDs
  const categoryNameToIdMap = new Map();
  availableCategories.forEach(cat => {
    categoryNameToIdMap.set(cat.name, cat.id || cat._id || cat.name);
  });

  // Convertir categorías de nombres a IDs
  if (providerData.categories && providerData.categories.length > 0) {
    preparedData.categories = providerData.categories.map(categoryName => {
      return categoryNameToIdMap.get(categoryName) || categoryName;
    });
  }

  // Convertir subcategorías al formato categoryId-subcategory
  if (providerData.subcategories && providerData.subcategories.length > 0) {
    preparedData.subcategories = providerData.subcategories.map(sub => {
      if (typeof sub !== 'string') return sub;

      // Si ya contiene un ID (formato uuid o similar), mantenerlo
      const parts = sub.split('-');
      if (parts.length >= 2) {
        const categoryPart = parts[0];
        const subcategoryPart = parts.slice(1).join('-');

        // Si categoryPart es un nombre de categoría, convertir a ID
        const categoryId = categoryNameToIdMap.get(categoryPart) || categoryPart;
        return `${categoryId}-${subcategoryPart}`;
      }

      return sub;
    });
  }

  return preparedData;
};

/**
 * 🔧 Función para limpiar nombres de subcategorías (versión mejorada)
 * Compatible con formatos: "uuid-...-Subcategory", "Category-Subcategory", "Subcategory"
 * Siempre usa el ÚLTIMO guión para separar el nombre de la subcategoría
 * @param {String} subcategory - Subcategoría a limpiar
 * @returns {String} Nombre limpio de la subcategoría
 */
export const cleanSubcategoryName = (subcategory) => {
  if (typeof subcategory !== 'string') return subcategory;

  // Eliminar sufijos de sugerencia
  let cleaned = subcategory.replace(' (sugerencia)', '').replace(/ \(sugerencia - .*\)$/, '');

  // Si contiene guiones, usar el ÚLTIMO guión para separar
  if (cleaned.includes('-')) {
    const lastDashIndex = cleaned.lastIndexOf('-');
    if (lastDashIndex !== -1) {
      return cleaned.substring(lastDashIndex + 1); // Parte después del último guión
    }
  }

  return cleaned;
};

/**
 * 🎯 Función para filtrar y mostrar subcategorías únicas de un proveedor
 * Maneja duplicados y prioriza "DE URGENCIA"
 * @param {Array} providerSubcategories - Subcategorías del proveedor
 * @param {Array} availableSubcategories - Subcategorías disponibles para la categoría
 * @returns {Array} Array de objetos con subcategorías únicas y limpias
 */
export const getUniqueDisplaySubcategories = (providerSubcategories, availableSubcategories) => {
  if (!providerSubcategories || !Array.isArray(providerSubcategories)) return [];

  // Filtrar subcategorías que coinciden con las disponibles
  const filteredSubs = providerSubcategories.filter((sub) =>
    availableSubcategories.some(subcat =>
      cleanSubcategoryName(sub) === subcat || sub === subcat
    )
  );

  // Eliminar duplicados
  const uniqueSubNames = new Set();
  const uniqueSubs = [];
  let hasUrgency = false;
  let urgencySubInfo = null;

  // Procesar subcategorías
  filteredSubs.forEach(sub => {
    const cleanedName = cleanSubcategoryName(sub);
    if (!uniqueSubNames.has(cleanedName)) {
      uniqueSubNames.add(cleanedName);

      // Manejar subcategoría de urgencia por separado
      if (cleanedName === "DE URGENCIA") {
        hasUrgency = true;
        urgencySubInfo = { original: sub, cleaned: cleanedName };
      } else {
        uniqueSubs.push({ original: sub, cleaned: cleanedName });
      }
    }
  });

  // Agregar urgencia al principio si existe
  if (hasUrgency) {
    uniqueSubs.unshift(urgencySubInfo);
  }

  return uniqueSubs;
};

/**
 * 📊 Función para obtener estado de migración (para debugging/admin)
 * @param {Array} providers - Lista de proveedores
 * @returns {Object} Estadísticas de migración
 */
export const getMigrationStats = (providers) => {
  if (!providers || !Array.isArray(providers)) {
    return { total: 0, migrated: 0, pending: 0, percentage: 0 };
  }

  const total = providers.length;
  const migrated = providers.filter(isProviderMigrated).length;
  const pending = total - migrated;
  const percentage = total > 0 ? Math.round((migrated / total) * 100) : 0;

  return { total, migrated, pending, percentage };
};

/**
 * 🔍 Función helper para debugging - mostrar formato de datos
 * @param {Object} provider - Proveedor para analizar
 * @returns {Object} Información de debug
 */
export const debugProviderFormat = (provider) => {
  return {
    username: provider?.username || 'N/A',
    isMigrated: isProviderMigrated(provider),
    hasCategoryIds: !!(provider?.categoryIds?.length),
    hasCategoryNames: !!(provider?.categoryNames?.length),
    hasCategories: !!(provider?.categories?.length),
    subcategoriesCount: provider?.subcategories?.length || 0,
    subcategoriesFormat: provider?.subcategories?.slice(0, 2) || [], // Primeras 2 para analizar formato
  };
};
